import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import HCLogo from '../components/HCLogo';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import {
  ArrowLeft, Building2, Mail, FileText, Euro, Package, Clock, Users,
  TrendingUp, Tag, Award, BarChart3, LogOut, Factory, Download, FileSpreadsheet
} from 'lucide-react';
import { apiCustomerDetail, apiProdExport } from '../api';
import { toast } from 'sonner';

const fmtEur = (v) => '€' + (v || 0).toFixed(2);
const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

// Chart constants extracted to avoid creating new object references on every render
const LINE_CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const BAR_CHART_MARGIN = { top: 5, right: 16, left: 60, bottom: 5 };
const AXIS_TICK_SM = { fontSize: 11 };
const AXIS_TICK_XS = { fontSize: 10 };
const TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 };
const BAR_RADIUS_RIGHT = [0, 4, 4, 0];
const HTTP_NOT_FOUND = 404;
const LINE_CHART_HEIGHT = 280;
const BAR_ROW_HEIGHT_PX = 36;
const BAR_MIN_HEIGHT_PX = 180;
const OBJECT_URL_REVOKE_DELAY_MS = 1000;

const KPICard = ({ icon: Icon, label, value, color, sublabel }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xl font-bold text-slate-900 tabular-nums leading-tight truncate">{value}</div>
        <div className="text-xs text-slate-500 leading-tight">{label}</div>
        {sublabel && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{sublabel}</div>}
      </div>
    </div>
  </div>
);

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiCustomerDetail(customerId, period)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === HTTP_NOT_FOUND) {
          toast.error('Cliente non trovato');
          navigate('/production', { replace: true });
        } else {
          toast.error('Errore caricamento dettaglio cliente');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, period, navigate]);

  const exportFile = async (fmt) => {
    try {
      const resp = await apiProdExport({ fmt, customer_id: customerId });
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `cliente_${customerId}.${fmt}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Esportato ${fmt.toUpperCase()}`);
    } catch {
      toast.error('Errore export');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 border-b border-slate-200 bg-white px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <HCLogo size="md" />
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">HC Produzione</div>
            <div className="text-[11px] text-slate-500 leading-tight">Dettaglio cliente</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center text-xs font-semibold">{user?.initials}</div>
            <div className="text-xs">
              <div className="font-medium text-slate-900">{user?.name}</div>
              <div className="text-slate-500 flex items-center gap-1"><Factory className="w-3 h-3" />Produzione</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5">
            <LogOut className="w-4 h-4" /> Esci
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6 space-y-5">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate('/production')}
          className="gap-1.5 -ml-2"
          data-testid="back-to-production-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
        </Button>

        {loading || !data ? (
          <div className="text-center py-20 text-sm text-slate-500">Caricamento dettaglio cliente...</div>
        ) : (
          <>
            {/* Customer header card */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center">
                    <Building2 className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{data.customer.code}</span>
                      {!data.customer.active && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Disattivato</span>
                      )}
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900" data-testid="customer-name-heading">{data.customer.name}</h1>
                    {data.customer.referente && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-1">
                        <Mail className="w-3.5 h-3.5" />
                        <span>Referente: <strong>{data.customer.referente}</strong></span>
                      </div>
                    )}
                    {data.customer.notes && (
                      <div className="flex items-start gap-1.5 text-sm text-slate-500 mt-2 max-w-xl">
                        <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span className="italic">{data.customer.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="customer-export-csv" variant="outline" onClick={() => exportFile('csv')} className="gap-2 h-9 text-sm">
                    <Download className="w-4 h-4" /> CSV
                  </Button>
                  <Button data-testid="customer-export-xlsx" variant="outline" onClick={() => exportFile('xlsx')} className="gap-2 h-9 text-sm">
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </Button>
                </div>
              </div>
            </div>

            {/* Period filter */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center bg-white border border-slate-200 rounded-md p-0.5">
                {[
                  { v: 'day', label: 'Oggi' },
                  { v: 'week', label: 'Settimana' },
                  { v: 'month', label: 'Mese' },
                  { v: '3months', label: '3 mesi' },
                  { v: 'all', label: 'Tutto' }
                ].map(o => (
                  <button
                    key={o.v}
                    data-testid={`customer-period-${o.v}`}
                    onClick={() => setPeriod(o.v)}
                    className={`text-xs px-3 py-1.5 rounded transition-colors ${
                      period === o.v ? 'bg-blue-700 text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >{o.label}</button>
                ))}
              </div>
              <div className="text-xs text-slate-500">
                Periodo: {data.start_date} → {data.end_date}
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KPICard icon={Package} label="Pezzi totali" value={data.kpi.total_pieces} color="bg-blue-100 text-blue-700" />
              <KPICard icon={Clock} label="Ore lavorate" value={`${data.kpi.total_hours.toFixed(1)}h`} color="bg-amber-100 text-amber-700" />
              <KPICard icon={Euro} label="Totale fatturato" value={fmtEur(data.kpi.total_eur)} color="bg-emerald-100 text-emerald-700" />
              <KPICard icon={TrendingUp} label="Media €/ora" value={fmtEur(data.kpi.avg_eur_per_hour)} color="bg-indigo-100 text-indigo-700" />
              <KPICard icon={BarChart3} label="Sessioni" value={data.kpi.sessions} color="bg-slate-100 text-slate-700" />
              <KPICard icon={Users} label="Operatori coinvolti" value={data.kpi.operators_count} color="bg-rose-100 text-rose-700" />
              <KPICard icon={Tag} label="Lavorazioni distinte" value={data.kpi.work_types_count} color="bg-violet-100 text-violet-700" />
              <KPICard
                icon={Award}
                label="Lavorazione top"
                value={data.kpi.top_work_type ? data.kpi.top_work_type.work_type_code : '—'}
                sublabel={data.kpi.top_work_type ? `${fmtEur(data.kpi.top_work_type.total_eur)} su ${data.kpi.top_work_type.pieces} pz` : 'Nessun dato'}
                color="bg-amber-100 text-amber-700"
              />
            </div>

            {/* Monthly trend */}
            {data.monthly_trend.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-sm font-semibold text-slate-900 mb-3">Andamento mensile (ultimi 6 mesi)</div>
                <ResponsiveContainer width="100%" height={LINE_CHART_HEIGHT}>
                  <LineChart data={data.monthly_trend} margin={LINE_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tickFormatter={fmtMonth} tick={AXIS_TICK_SM} stroke="#94a3b8" />
                    <YAxis tick={AXIS_TICK_SM} stroke="#94a3b8" />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      labelFormatter={fmtMonth}
                      formatter={(v, name) => {
                        if (name === 'Totale €') return [fmtEur(v), name];
                        if (name === 'Media €/ora') return [fmtEur(v), name];
                        return [v, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="total_eur" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4 }} name="Totale €" />
                    <Line type="monotone" dataKey="avg_eur_per_hour" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} name="Media €/ora" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Operators ranking + Work types breakdown side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Operators */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" /> Classifica operatori
                  </div>
                  <div className="text-xs text-slate-500">{data.operators.length}</div>
                </div>
                {data.operators.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-500">Nessun operatore ha lavorato per questo cliente</div>
                ) : (
                  <>
                    <div className="p-3 border-b border-slate-100">
                      <ResponsiveContainer width="100%" height={Math.max(BAR_MIN_HEIGHT_PX, data.operators.length * BAR_ROW_HEIGHT_PX)}>
                        <BarChart data={data.operators.slice(0, 10)} layout="vertical" margin={BAR_CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={AXIS_TICK_XS} stroke="#94a3b8" />
                          <YAxis type="category" dataKey="operator_alias" tick={AXIS_TICK_SM} stroke="#475569" width={60} />
                          <Tooltip
                            contentStyle={TOOLTIP_STYLE}
                            formatter={(v) => [fmtEur(v), 'Totale €']}
                          />
                          <Bar dataKey="total_eur" fill="#3b82f6" radius={BAR_RADIUS_RIGHT} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">#</th>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Operatore</th>
                          <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Pezzi</th>
                          <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ore</th>
                          <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Totale</th>
                          <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">€/ora</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {data.operators.map((o, idx) => (
                          <tr key={o.operator_id} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-500 font-medium">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-1.5">{o.operator_alias}</span>
                              <span className="text-slate-700 text-xs">{o.operator_name}</span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-xs">{o.pieces}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-xs">{o.hours.toFixed(1)}h</td>
                            <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-blue-700">{fmtEur(o.total_eur)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-emerald-700">{fmtEur(o.avg_eur_per_hour)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>

              {/* Work types */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-slate-500" /> Lavorazioni
                  </div>
                  <div className="text-xs text-slate-500">{data.work_types.length}</div>
                </div>
                {data.work_types.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-500">Nessuna lavorazione registrata</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Codice</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Descrizione</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">€/pz</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Pezzi</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ore</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.work_types.map(w => (
                        <tr key={w.work_type_id} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs font-medium text-slate-900">{w.work_type_code}</td>
                          <td className="px-3 py-2 text-xs text-slate-700 truncate max-w-[180px]">{w.work_type_description}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs">€{(w.rate_eur_per_piece || 0).toFixed(3)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs">{w.pieces}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs">{w.hours.toFixed(1)}h</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-blue-700">{fmtEur(w.total_eur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default CustomerDetailPage;
